/* eslint-disable */
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  Divider,
  MenuItem,
  TextField,
  Typography,
  IconButton,
  useMediaQuery,
  Chip,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useEffect, useState } from "react";
import ApiEndpoint from "../../config/ApiEndpoint";
import ApiServices from "../../config/ApiServices";
import { toast } from "react-toastify";
import styles from "./MasterOrgPages.module.scss";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { TouchFriendlyTooltip } from "../shared";
import { apiFlagIsTrue } from "../../Utils/func";

const EMPTY_CLUB = () => ({ name: "", email: "" });
const INVITE_PERMISSION_MSG = "not permitted to invite clubs";

const InviteCodes = (props) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const token = props?.props?.profile?.token;
  const userType =
    props?.props?.profile?.user_type || props?.props?.profile?.data?.user_type;
  const canInviteClubsRaw = props?.props?.profile?.can_invite_clubs;
  const canInviteClubs =
    canInviteClubsRaw == null ? true : apiFlagIsTrue(canInviteClubsRaw);
  const saveUserData = props?.props?.save_user_data;

  const handleInvitePermissionDenied = (data) => {
    const message = data?.message || "";
    if (message.toLowerCase().includes(INVITE_PERMISSION_MSG) && saveUserData) {
      saveUserData({
        user: { ...props?.props?.profile, can_invite_clubs: false },
      });
    }
  };
  const [statusFilter, setStatusFilter] = useState("active");
  const [codes, setCodes] = useState([]);
  const [openCreate, setOpenCreate] = useState(false);
  const [expiresAt, setExpiresAt] = useState("");
  const [maxUses, setMaxUses] = useState("");
  const [openSendInvite, setOpenSendInvite] = useState(false);
  const [clubs, setClubs] = useState([EMPTY_CLUB()]);
  const [sendingInvite, setSendingInvite] = useState(false);
  const [deleteCodeId, setDeleteCodeId] = useState(null);
  const [deletingCode, setDeletingCode] = useState(false);

  const loadCodes = async () => {
    if (!token) return;
    const headers = {
      "Content-Type": "application/json",
      "x-access-token": token,
    };
    const statusQuery = statusFilter ? `?status=${statusFilter}` : "";
    props.setIsLoaded(true);
    const data = await ApiServices.GetApiCall(
      `${ApiEndpoint.MASTER_ORG_CODES_LIST}${statusQuery}`,
      headers
    );
    props.setIsLoaded(false);
    if (data?.status) {
      setCodes(data.data || []);
    } else if (data?.message) {
      toast.error(data.message);
    }
  };

  const handleClubChange = (index, field, value) => {
    setClubs((prev) =>
      prev.map((club, i) => (i === index ? { ...club, [field]: value } : club))
    );
  };

  const handleAddClub = () => setClubs((prev) => [...prev, EMPTY_CLUB()]);

  const handleRemoveClub = (index) =>
    setClubs((prev) => prev.filter((_, i) => i !== index));

  const handleSendInvite = async () => {
    if (!token) return;

    const validClubs = clubs.filter(
      (c) => c.name.trim() && c.email.trim()
    );
    if (validClubs.length === 0) {
      toast.error("Add at least one club with a name and email");
      return;
    }
    const invalidClubs = clubs.filter(
      (c) => (c.name.trim() && !c.email.trim()) || (!c.name.trim() && c.email.trim())
    );
    if (invalidClubs.length > 0) {
      toast.error("Each club entry must have both a name and an email");
      return;
    }

    const headers = {
      "Content-Type": "application/json",
      "x-access-token": token,
    };
    const emailSubject = "You've been invited to join SportsApp";
    const emailBody = `Hello,\n\nYour association has invited you to join SportsApp. Your unique code is {{CODE}} — simply enter this when registering.\n\nOnce your account is set up, you can invite your members to join your page and communicate with them via push notifications. You can also use SportsApp to find coaches, players, and parents through the "My Connect" feature, and invite new members to join your page.\n\nOnly you and your association can view your members' details — all information remains private on the platform.\n\nIf you choose not to actively use SportsApp, we recommend at least inviting your members so your association can communicate important updates to them and share key information with you via desktop.\n\nNote: This invite was sent to this email address, but you can change your email address after registering in your account settings.`;

    setSendingInvite(true);
    const results = await Promise.allSettled(
      validClubs.map((club) =>
        ApiServices.PostApiCall(
          ApiEndpoint.MASTER_ORG_CODES_SEND_INVITE,
          JSON.stringify({
            club_name: club.name.trim(),
            club_email: club.email.trim(),
            email_subject: emailSubject,
            email_body: emailBody,
          }),
          headers
        )
      )
    );
    setSendingInvite(false);

    const succeeded = results.filter(
      (r) => r.status === "fulfilled" && r.value?.status
    ).length;
    const failed = results.length - succeeded;

    if (succeeded > 0 && failed === 0) {
      toast.success(
        `Invite${succeeded > 1 ? "s" : ""} sent successfully to ${succeeded} club${succeeded > 1 ? "s" : ""}`
      );
    } else if (succeeded > 0) {
      toast.success(`${succeeded} invite${succeeded > 1 ? "s" : ""} sent`);
      toast.error(`${failed} invite${failed > 1 ? "s" : ""} failed`);
    } else {
      const firstError = results.find((r) => r.status === "fulfilled")?.value;
      handleInvitePermissionDenied(firstError);
      toast.error(firstError?.message || "Failed to send invites");
    }

    if (succeeded > 0) {
      setOpenSendInvite(false);
      setClubs([EMPTY_CLUB()]);
      loadCodes();
    }
  };

  const handleCreate = async () => {
    if (!token) return;
    if (!maxUses) {
      toast.error("Max uses is required");
      return;
    }
    const headers = {
      "Content-Type": "application/json",
      "x-access-token": token,
    };
    const body = { max_uses: Number(maxUses) };
    if (expiresAt) body.expires_at = expiresAt;
    props.setIsLoaded(true);
    const data = await ApiServices.PostApiCall(
      ApiEndpoint.MASTER_ORG_CODES_CREATE,
      JSON.stringify(body),
      headers
    );
    props.setIsLoaded(false);
    if (data?.status) {
      toast.success(data.message || "Code created");
      setOpenCreate(false);
      setExpiresAt("");
      setMaxUses("");
      loadCodes();
    } else {
      handleInvitePermissionDenied(data);
      toast.error(data?.message || "Failed to create code");
    }
  };

  const handleDeleteCode = async () => {
    if (!token || !deleteCodeId) return;
    const headers = {
      "Content-Type": "application/json",
      "x-access-token": token,
    };
    setDeletingCode(true);
    const data = await ApiServices.PostApiCall(
      ApiEndpoint.MASTER_ORG_CODES_DELETE,
      JSON.stringify({ code_id: deleteCodeId }),
      headers
    );
    setDeletingCode(false);
    if (data?.status) {
      toast.success(data.message || "Code deleted");
      setDeleteCodeId(null);
      loadCodes();
    } else {
      toast.error(data?.message || "Failed to delete code");
    }
  };

  useEffect(() => {
    if (userType === "master_org") {
      loadCodes();
    }
  }, [userType, token, statusFilter]);

  // Hide fully-redeemed codes when viewing active ones; show all under other filters
  const visibleCodes =
    statusFilter === "active"
      ? codes.filter((code) => Number(code.uses) < Number(code.max_uses))
      : codes;

  if (userType !== "master_org") {
    return (
      <Box className={styles.container}>
        <Typography className={styles.title}>Invite Codes</Typography>
        <Typography className={styles.subtitle}>
          Access denied. This page is for club partner accounts.
        </Typography>
      </Box>
    );
  }

  return (
    <>
      <Box className={styles.container} sx={{ px: { xs: 2, sm: 3 }, py: { xs: 2, sm: 3 } }}>
        <Box
          className={styles.headerRow}
          sx={{
            flexDirection: { xs: "column", sm: "row" },
            gap: { xs: 1.5, sm: 0 },
            alignItems: { xs: "stretch", sm: "flex-start" },
          }}
        >
          <Box>
            <Typography
              className={styles.title}
              sx={{ color: isDark ? "#ffffff" : "#1c1c1c" }}
            >
              Invite Codes
            </Typography>
            <Typography
              className={styles.subtitle}
              sx={{ color: isDark ? "#cbd5e1" : "#6f6f6f" }}
            >
              Create and manage invite codes for clubs.
            </Typography>
            {!canInviteClubs && (
              <Typography
                sx={{
                  mt: 0.75,
                  fontSize: "0.8125rem",
                  color: isDark ? "#fbbf24" : "#b45309",
                }}
              >
                Inviting clubs is not enabled for your club partner. Contact your admin to enable it.
              </Typography>
            )}
          </Box>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              flexWrap: "wrap",
            }}
          >
            <TouchFriendlyTooltip
              title={
                <Box sx={{ maxWidth: 320 }}>
                  <Typography sx={{ fontWeight: 600, mb: 0.5 }}>
                    How invite codes work
                  </Typography>
                  <Typography variant="body2">
                    • Generate a code and share it with a club.
                  </Typography>
                  <Typography variant="body2">
                    • They use the code during sign up to request connection as
                    your club.
                  </Typography>
                  <Typography variant="body2">
                    • Their request then appears on the{" "}
                    <strong>Join Requests</strong> page for approval.
                  </Typography>
                </Box>
              }
              arrow
            >
              <IconButton
                size="small"
                sx={{ color: isDark ? "#cbd5e1" : "#4a5568" }}
              >
                <HelpOutlineIcon fontSize="small" />
              </IconButton>
            </TouchFriendlyTooltip>
            {canInviteClubs ? (
              <Button
                className={styles.primaryButton}
                size={isMobile ? "small" : "medium"}
                onClick={() => setOpenSendInvite(true)}
                sx={{
                  px: isMobile ? 1.5 : 2,
                  py: isMobile ? 0.75 : 1,
                  fontSize: isMobile ? "0.8125rem" : undefined,
                }}
              >
                Invite Clubs
              </Button>
            ) : (
              <TouchFriendlyTooltip
                title="Club-invite capability is disabled. Ask your admin to enable inviting clubs for your club."
                arrow
              >
                <Box component="span" sx={{ display: "inline-flex" }}>
                  <Button
                    className={styles.primaryButton}
                    size={isMobile ? "small" : "medium"}
                    disabled
                    sx={{
                      px: isMobile ? 1.5 : 2,
                      py: isMobile ? 0.75 : 1,
                      fontSize: isMobile ? "0.8125rem" : undefined,
                    }}
                  >
                    Invite Clubs
                  </Button>
                </Box>
              </TouchFriendlyTooltip>
            )}
          </Box>
        </Box>

        <Box className={styles.filterRow}>
          <TextField
            select
            label="Status"
            value={statusFilter}
            onChange={(event) => setStatusFilter(event.target.value)}
            size={isMobile ? "small" : "medium"}
            sx={{ minWidth: { xs: "100%", sm: 160 } }}
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="active">Active</MenuItem>
            <MenuItem value="expired">Expired</MenuItem>
            <MenuItem value="disabled">Disabled</MenuItem>
          </TextField>
        </Box>

        <Box className={styles.list}>
          {visibleCodes.length === 0 ? (
            <Typography
              className={styles.emptyState}
              sx={{ color: isDark ? "#cbd5e1" : "#6f6f6f" }}
            >
              No invite codes found.
            </Typography>
          ) : (
            visibleCodes.map((code) => (
              <Box
                key={code.id}
                sx={{
                  backgroundColor: isDark ? "#242424" : "#ffffff",
                  border: `1px solid ${isDark ? "rgba(255,255,255,0.1)" : "#f0f0f0"}`,
                  borderRadius: "12px",
                  overflow: "hidden",
                }}
              >
                {/* Card info section */}
                <Box sx={{ px: { xs: 2, sm: 2.5 }, pt: { xs: 1.75, sm: 2 }, pb: 1.5 }}>
                  {/* Code name row */}
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, mb: 0.75 }}>
                    <Typography
                      sx={{
                        fontFamily: "monospace",
                        fontWeight: 700,
                        fontSize: { xs: "0.9375rem", sm: "1rem" },
                        letterSpacing: "0.04em",
                        color: isDark ? "#ffffff" : "#1c1c1c",
                        wordBreak: "break-all",
                      }}
                    >
                      {code.code}
                    </Typography>
                    {code.sent_to_email && (
                      <Chip
                        label="Sent"
                        size="small"
                        sx={{
                          flexShrink: 0,
                          height: 20,
                          fontSize: "11px",
                          fontWeight: 600,
                          backgroundColor: isDark ? "rgba(37,99,235,0.25)" : "#dbeafe",
                          color: isDark ? "#93c5fd" : "#1d4ed8",
                          "& .MuiChip-label": { px: 1 },
                        }}
                      />
                    )}
                  </Box>

                  {/* Club / email details */}
                  {code.sent_to_club && (
                    <Typography sx={{ fontSize: "12px", color: isDark ? "#94a3b8" : "#7a7a7a", mb: 0.25 }}>
                      Club: {code.sent_to_club}
                    </Typography>
                  )}
                  {code.sent_to_email && (
                    <Typography sx={{ fontSize: "12px", color: isDark ? "#94a3b8" : "#7a7a7a", mb: 0.25 }}>
                      {code.sent_to_email}
                    </Typography>
                  )}

                  {/* Expires + Uses on one row */}
                  <Box sx={{ display: "flex", gap: { xs: 2, sm: 3 }, mt: 0.5 }}>
                    <Typography sx={{ fontSize: "12px", color: isDark ? "#94a3b8" : "#7a7a7a" }}>
                      Expires: {code.expires_at || "No expiry"}
                    </Typography>
                    <Typography sx={{ fontSize: "12px", color: isDark ? "#94a3b8" : "#7a7a7a" }}>
                      Uses: {code.uses}/{code.max_uses}
                    </Typography>
                  </Box>
                </Box>

                {/* Divider + action row */}
                <Divider sx={{ borderColor: isDark ? "rgba(255,255,255,0.07)" : "#f0f0f0" }} />
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    px: { xs: 1.5, sm: 2 },
                    py: { xs: 0.75, sm: 1 },
                    gap: 1,
                  }}
                >
                  <Button
                    startIcon={<ContentCopyIcon sx={{ fontSize: "16px !important" }} />}
                    size="small"
                    onClick={() => {
                      navigator.clipboard.writeText(code.code);
                      toast.success("Code copied");
                    }}
                    sx={{
                      flex: 1,
                      textTransform: "none",
                      fontWeight: 600,
                      fontSize: "0.8125rem",
                      backgroundColor: isDark ? "rgba(255,255,255,0.08)" : "#f5f5f5",
                      color: isDark ? "#e2e8f0" : "#374151",
                      borderRadius: "8px",
                      py: 0.875,
                      "&:hover": {
                        backgroundColor: isDark ? "rgba(255,255,255,0.13)" : "#e9e9e9",
                      },
                    }}
                  >
                    Copy Code
                  </Button>
                  {isMobile ? (
                    <IconButton
                      size="small"
                      onClick={() => setDeleteCodeId(code.id)}
                      sx={{
                        borderRadius: "8px",
                        p: 1,
                        backgroundColor: isDark ? "rgba(239,68,68,0.12)" : "#fee2e2",
                        color: isDark ? "#fca5a5" : "#b91c1c",
                        "&:hover": {
                          backgroundColor: isDark ? "rgba(239,68,68,0.22)" : "#fecaca",
                        },
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  ) : (
                    <Button
                      size="small"
                      onClick={() => setDeleteCodeId(code.id)}
                      sx={{
                        textTransform: "none",
                        fontWeight: 600,
                        fontSize: "0.8125rem",
                        borderRadius: "8px",
                        py: 0.875,
                        px: 2,
                        backgroundColor: isDark ? "rgba(239,68,68,0.12)" : "#fee2e2",
                        color: isDark ? "#fca5a5" : "#b91c1c",
                        "&:hover": {
                          backgroundColor: isDark ? "rgba(239,68,68,0.22)" : "#fecaca",
                        },
                      }}
                    >
                      Delete
                    </Button>
                  )}
                </Box>
              </Box>
            ))
          )}
        </Box>
      </Box>

      {/* Bulk Send Invite Dialog */}
      <Dialog
        open={openSendInvite}
        onClose={() => {
          setOpenSendInvite(false);
          setClubs([EMPTY_CLUB()]);
        }}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            backgroundColor: isDark ? "#1a1a1a" : "#ffffff",
            border: isDark ? "1px solid rgba(255, 255, 255, 0.05)" : "none",
          },
        }}
      >
        <Box
          className={styles.dialogBody}
          sx={{ backgroundColor: isDark ? "#1a1a1a" : "#ffffff" }}
        >
          <Typography
            className={styles.sectionTitle}
            sx={{ color: isDark ? "#ffffff" : "#1c1c1c" }}
          >
            Send Invites to Clubs
          </Typography>
          <Typography
            className={styles.subtitle}
            sx={{ color: isDark ? "#cbd5e1" : "#6f6f6f" }}
          >
            A unique invite code will be generated and emailed to each club.
          </Typography>
          <Divider sx={{ my: 1.5 }} />

          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {clubs.map((club, index) => (
              <Box
                key={index}
                sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}
              >
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 1,
                    flex: 1,
                  }}
                >
                  <TextField
                    label="Club Name"
                    value={club.name}
                    onChange={(e) =>
                      handleClubChange(index, "name", e.target.value)
                    }
                    fullWidth
                    size="small"
                  />
                  <TextField
                    label="Club Email"
                    type="email"
                    value={club.email}
                    onChange={(e) =>
                      handleClubChange(index, "email", e.target.value)
                    }
                    fullWidth
                    size="small"
                  />
                </Box>
                {clubs.length > 1 && (
                  <IconButton
                    onClick={() => handleRemoveClub(index)}
                    size="small"
                    sx={{
                      mt: 0.5,
                      color: isDark ? "#fca5a5" : "#b91c1c",
                    }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                )}
              </Box>
            ))}
          </Box>

          <Button
            startIcon={<AddIcon />}
            onClick={handleAddClub}
            size="small"
            sx={{
              mt: 1.5,
              color: isDark ? "#93c5fd" : "#1d4ed8",
              textTransform: "none",
              pl: 0,
            }}
          >
            Add another club
          </Button>

          <Box className={styles.dialogActions} sx={{ mt: 2 }}>
            <Button
              className={styles.secondaryButton}
              size={isMobile ? "small" : "medium"}
              onClick={() => {
                setOpenSendInvite(false);
                setClubs([EMPTY_CLUB()]);
              }}
              sx={{ px: isMobile ? 1.5 : 2, py: isMobile ? 0.75 : 1 }}
            >
              Cancel
            </Button>
            <Button
              className={styles.primaryButton}
              size={isMobile ? "small" : "medium"}
              onClick={handleSendInvite}
              disabled={sendingInvite}
              sx={{
                px: isMobile ? 1.5 : 2,
                py: isMobile ? 0.75 : 1,
                minWidth: 120,
              }}
            >
              {sendingInvite ? (
                <CircularProgress size={18} sx={{ color: "#fff" }} />
              ) : clubs.length > 1 ? (
                `Send ${clubs.filter((c) => c.name.trim() && c.email.trim()).length || ""} Invites`
              ) : (
                "Send Invite"
              )}
            </Button>
          </Box>
        </Box>
      </Dialog>

      {/* Create Code Dialog */}
      <Dialog
        open={openCreate}
        onClose={() => setOpenCreate(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            backgroundColor: isDark ? "#1a1a1a" : "#ffffff",
            border: isDark ? "1px solid rgba(255, 255, 255, 0.05)" : "none",
          },
        }}
      >
        <Box
          className={styles.dialogBody}
          sx={{ backgroundColor: isDark ? "#1a1a1a" : "#ffffff" }}
        >
          <Typography
            className={styles.sectionTitle}
            sx={{ color: isDark ? "#ffffff" : "#1c1c1c" }}
          >
            Create Invite Code
          </Typography>
          <Typography
            className={styles.subtitle}
            sx={{ color: isDark ? "#cbd5e1" : "#6f6f6f" }}
          >
            Set an expiry date and usage limit.
          </Typography>
          <Divider />
          <Box className={styles.formGrid}>
            <TextField
              label="Max Uses"
              value={maxUses}
              onChange={(event) => setMaxUses(event.target.value)}
              fullWidth
              sx={{ marginBottom: "12px" }}
            />
            <TextField
              label="Expires At (optional)"
              type="datetime-local"
              InputLabelProps={{ shrink: true }}
              value={expiresAt}
              onChange={(event) => setExpiresAt(event.target.value)}
              fullWidth
            />
          </Box>
          <Box className={styles.dialogActions}>
            <Button
              className={styles.secondaryButton}
              size={isMobile ? "small" : "medium"}
              onClick={() => setOpenCreate(false)}
              sx={{ px: isMobile ? 1.5 : 2, py: isMobile ? 0.75 : 1 }}
            >
              Cancel
            </Button>
            <Button
              className={styles.primaryButton}
              size={isMobile ? "small" : "medium"}
              onClick={handleCreate}
              sx={{ px: isMobile ? 1.5 : 2, py: isMobile ? 0.75 : 1 }}
            >
              Create
            </Button>
          </Box>
        </Box>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={!!deleteCodeId}
        onClose={() => setDeleteCodeId(null)}
        fullWidth
        maxWidth="xs"
        PaperProps={{
          sx: {
            backgroundColor: isDark ? "#1a1a1a" : "#ffffff",
            border: isDark ? "1px solid rgba(255, 255, 255, 0.05)" : "none",
          },
        }}
      >
        <Box
          className={styles.dialogBody}
          sx={{ backgroundColor: isDark ? "#1a1a1a" : "#ffffff" }}
        >
          <Typography
            className={styles.sectionTitle}
            sx={{ color: isDark ? "#ffffff" : "#1c1c1c" }}
          >
            Delete Code
          </Typography>
          <Typography
            className={styles.subtitle}
            sx={{ color: isDark ? "#cbd5e1" : "#6f6f6f" }}
          >
            Are you sure you want to delete this invite code? This cannot be
            undone.
          </Typography>
          <Box className={styles.dialogActions} sx={{ mt: 2 }}>
            <Button
              className={styles.secondaryButton}
              size={isMobile ? "small" : "medium"}
              onClick={() => setDeleteCodeId(null)}
              sx={{ px: isMobile ? 1.5 : 2, py: isMobile ? 0.75 : 1 }}
            >
              Cancel
            </Button>
            <Button
              size={isMobile ? "small" : "medium"}
              onClick={handleDeleteCode}
              disabled={deletingCode}
              sx={{
                px: isMobile ? 1.5 : 2,
                py: isMobile ? 0.75 : 1,
                minWidth: 100,
                backgroundColor: "#dc2626",
                color: "#ffffff",
                "&:hover": { backgroundColor: "#b91c1c" },
                "&:disabled": { backgroundColor: "#fca5a5" },
              }}
            >
              {deletingCode ? (
                <CircularProgress size={18} sx={{ color: "#fff" }} />
              ) : (
                "Delete"
              )}
            </Button>
          </Box>
        </Box>
      </Dialog>
    </>
  );
};

export default InviteCodes;
